import { useState } from "react"

import { format } from "date-fns"
import { useNavigate } from "react-router-dom"

import { useApplication } from "@/contexts/ApplicationContext"
import { useToast } from "@/hooks/use-toast"

import { useVehicleCollectionCloudFunctions } from "./useVehicleCollectionCloudFunctions"

/**
 * Hook for handling vehicle collection submission
 */
export const useVehicleCollectionSubmissionHandlers = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { scheduleVehicleCollection } = useVehicleCollectionCloudFunctions()
  const { application, refetch, setApplication } = useApplication()
  const { toast } = useToast()
  const navigate = useNavigate()

  /**
   * Handle vehicle collection scheduling
   * @param selectedDate The selected date and time for vehicle collection
   * @param sessionId The ID of the selected session
   * @param depot The depot information
   * @param skipNavigation Whether to skip navigation after success (for segment mode)
   */
  const handleVehicleCollectionSubmit = async (
    selectedDate: Date,
    sessionId: string,
    depot?: { name: string; address: string; city: string },
    skipNavigation = false,
  ) => {
    if (!application?.id) {
      toast({
        title: "Missing Information",
        description: "Application ID is required",
        variant: "destructive",
      })
      return
    }

    if (!selectedDate || !sessionId) {
      toast({
        title: "Missing Information",
        description: "Please select a date and time for your vehicle collection",
        variant: "destructive",
      })
      return
    }

    setIsSubmitting(true)

    try {
      // Schedule vehicle collection using cloud function
      const result = await scheduleVehicleCollection.mutateAsync({
        applicationId: application.id,
        sessionId,
        date: selectedDate,
      })

      if (!result.success) {
        toast({
          title: "Booking Failed",
          description: result.message || "Failed to schedule vehicle collection",
          variant: "destructive",
        })
        return
      }

      // Save depot information to application context if provided
      if (depot && application) {
        setApplication({
          ...application,
          vehicleCollection: {
            ...application.vehicleCollection,
            date: selectedDate.toISOString(),
            depot: {
              name: depot.name,
              address: depot.address,
              city: depot.city,
            },
          },
        })
      }

      // Refetch application data to ensure it's up to date
      await refetch()

      // Show success message
      toast({
        title: "Vehicle Collection Scheduled",
        description: `Your vehicle collection is scheduled for ${format(
          selectedDate,
          "EEEE, MMMM d, yyyy",
        )} at ${format(selectedDate, "h:mm a")}`,
        variant: "default",
      })

      // Navigate to confirmation page (unless in segment mode)
      if (!skipNavigation) {
        navigate("/vehicle-collection-confirmation")
      }
    } catch (error) {
      console.error("Error scheduling vehicle collection:", error)
      toast({
        title: "Booking Failed",
        description:
          error instanceof Error
            ? error.message
            : "There was an error booking your vehicle collection. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    handleVehicleCollectionSubmit,
    isSubmitting,
  }
}
